import {
  IPriorAuthCheckResponse,
  IPriorAuthListResponse,
  IPriorAuthListsResponse,
  IPriorAuthPreviewResponse,
} from "types/PriorAuth/PriorAuth";

import { api } from ".";

/**
 * CLE-2758 Part 1 — RTK Query layer for the prior-authorization library.
 *
 * Create and update send multipart when there is a file and JSON when there is not: a list is born
 * from an uploaded spreadsheet, but afterwards an operator edits its payers, plans and codes in a
 * dialog without re-uploading anything. The API accepts both on the same route.
 *
 * `payerIds` and `planKeywords` are JSON-encoded into the form data because multipart flattens
 * everything to strings.
 */

export interface IPriorAuthCheckRequest {
  payerId: number;
  /** The plan eligibility reported, or null when it has not been run yet. */
  planName: string | null;
  cptCodes: string[];
  /**
   * Needed only for the related-codes panel — co-occurrence is learned from one account's own
   * remits. Without it the badge still works and the panel is simply empty.
   */
  accountId?: string;
}

interface IPriorAuthListPayload {
  name: string;
  payerIds: number[];
  planKeywords: string[];
  /** Present when the operator edited the codes by hand. */
  cptCodes?: string[];
  /** Present when the operator picked a file; replaces the code list wholesale. */
  file?: File | null;
}

const toBody = (payload: IPriorAuthListPayload): FormData | object => {
  if (!payload.file) {
    return {
      name: payload.name,
      payerIds: payload.payerIds,
      planKeywords: payload.planKeywords,
      ...(payload.cptCodes !== undefined ? { cptCodes: payload.cptCodes } : {}),
    };
  }

  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("name", payload.name);
  formData.append("payerIds", JSON.stringify(payload.payerIds));
  formData.append("planKeywords", JSON.stringify(payload.planKeywords));
  return formData;
};

const queries = api.injectEndpoints({
  endpoints: (builder) => ({
    previewPriorAuthFile: builder.mutation<IPriorAuthPreviewResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "prior-auth-lists/preview",
          method: "POST",
          body: formData,
        };
      },
    }),
    /**
     * CLE-2766 — which of these codes need prior auth for the order's payer and plan.
     *
     * Deliberately NOT tagged with `PriorAuthLists`: an admin editing a list must not invalidate
     * and refetch this on every open estimate. The answer is advisory and a few minutes stale is
     * harmless; a refetch storm across the order flow is not.
     */
    checkPriorAuth: builder.query<
      IPriorAuthCheckResponse,
      IPriorAuthCheckRequest
    >({
      query: ({ payerId, planName, cptCodes, accountId }) => {
        const qs = new URLSearchParams();
        qs.set("payerId", String(payerId));
        if (planName) qs.set("planName", planName);
        if (accountId) qs.set("accountId", accountId);
        qs.set("cptCodes", JSON.stringify(cptCodes));
        return `prior-auth-lists/check?${qs.toString()}`;
      },
    }),
    getPriorAuthLists: builder.query<IPriorAuthListsResponse, void>({
      query: () => "prior-auth-lists",
      providesTags: ["PriorAuthLists"],
    }),
    getPriorAuthList: builder.query<IPriorAuthListResponse, string>({
      query: (id) => `prior-auth-lists/${id}`,
      providesTags: ["PriorAuthLists"],
    }),
    createPriorAuthList: builder.mutation<
      IPriorAuthListResponse,
      IPriorAuthListPayload
    >({
      query: (payload) => ({
        url: "prior-auth-lists",
        method: "POST",
        body: toBody(payload),
      }),
      invalidatesTags: ["PriorAuthLists"],
    }),
    updatePriorAuthList: builder.mutation<
      IPriorAuthListResponse,
      IPriorAuthListPayload & { id: string }
    >({
      query: ({ id, ...payload }) => ({
        url: `prior-auth-lists/${id}`,
        method: "PUT",
        body: toBody(payload),
      }),
      invalidatesTags: ["PriorAuthLists"],
    }),
    deletePriorAuthList: builder.mutation<
      { data: { id: string }; message: string },
      string
    >({
      query: (id) => ({ url: `prior-auth-lists/${id}`, method: "DELETE" }),
      invalidatesTags: ["PriorAuthLists"],
    }),
  }),
});

export const {
  useCheckPriorAuthQuery,
  usePreviewPriorAuthFileMutation,
  useGetPriorAuthListsQuery,
  useLazyGetPriorAuthListQuery,
  useCreatePriorAuthListMutation,
  useUpdatePriorAuthListMutation,
  useDeletePriorAuthListMutation,
} = queries;
